import api from '@/services/api';
import { IDIBU_DEMO_MODE } from '../demo/demo-flag';
import { demoIdibuApi } from '../demo/demo-api';

const BASE = '/v2/idibu';

export interface IdibuPortal {
    id: number;
    name: string;
    url: string;
    logo: string;
    tags: string;
    country: string;
    description?: string;
    'supports-aptrack'?: string;
    'supports-delete'?: string;
}

export interface IdibuLocation {
    id: number;
    place_name: string;
    country_code: string;
    postal_code: string;
    county: string;
    region: string;
    country: string;
}

export interface ResolvedLocation {
    countryCode: string;
    sublocationId: number | null;
    sublocationName: string | null;
    original: string | null;
}

export interface BoardPost {
    portalId: number | null;
    portalName: string | null;
    postLogId: number | null;
    status: 'pending' | 'posted' | 'updated' | 'deleted' | 'failed';
    url: string | null;
    expiryDate: string | null;
    lastUpdate: string | null;
    firstPostedAt: string | null;
    publishCount: number;
}

export interface BoardPublishing {
    provider: 'idibu';
    providerJobId: string | null;
    providerPostingId: string | null;
    status: 'draft' | 'posting' | 'live' | 'expired' | 'failed';
    pcpUrl: string | null;
    posts: BoardPost[];
    lastSyncedAt: string | null;
}

export interface PublishResult {
    pcpUrl: string | null;
    jobId: string;
    idibuResponse: unknown;
    resolvedLocation?: ResolvedLocation;
}

export interface IdibuOrgConfig {
    accountHash: string | null;
    clientId: number | null;
    isActive: boolean;
    provisionedByPartner: boolean;
    connectedAt: string | null;
    hasStoredHash?: boolean;
}

export interface IdibuConfigResponse {
    config: IdibuOrgConfig | null;
    partnerAvailable: boolean;
}

export interface ProvisionData {
    companyName: string;
    adminFirstname: string;
    adminLastname: string;
    adminEmail: string;
    username: string;
    password: string;
    country?: string;
    postcode?: string;
    phone?: string;
    website?: string;
}

export interface SubscribeFieldValue {
    name: string;
    value: string;
}

export interface SubscribeField {
    element: string;
    name: string;
    title: string;
    values?: SubscribeFieldValue[];
    order: number;
    required: 'yes' | 'no';
    maxchars?: number;
    type: 'general' | 'login';
}

export interface PortalCategory {
    category: string;
}

export interface PortalCountry {
    code: string;
    name: string;
}

export interface ProvisionedAccount {
    clientHash: string;
    clientId: number | null;
    username: string;
}

export interface ApplicantSyncResult {
    synced?: boolean;
    skipped?: boolean;
    reason?: string;
    idibuStatus?: string;
}

export interface ReconcileResult {
    total: number;
    created: number;
    existing: number;
    skipped: number;
    errors: number;
}

export interface PortalSubscribeInfoResponse {
    help: string | null;
    fields: SubscribeField[];
}

export const realIdibuApi = {
    listSubscribedPortals: () =>
        api.get<{ portals: IdibuPortal[] }>(`${BASE}/portals`),

    listAllPortals: (params?: { count?: number; offset?: number; filter_name?: string; filter_country?: string }) =>
        api.get<{ portals: IdibuPortal[] }>(`${BASE}/portals/all`, { params }),

    searchLocations: (countryCode: string, filterName: string, count?: number) =>
        api.get<{ locations: IdibuLocation[] }>(`${BASE}/locations/${countryCode}`, {
            params: { filter_name: filterName, ...(count ? { count } : {}) },
        }),

    publishJob: (data: {
        jobPostingId: string;
        redirectUrl?: string;
        completionEmail?: string;
        boards?: { id: number; durationDays?: number }[];
        countryCode?: string;
        sublocationId?: number;
    }) => api.post<PublishResult>(`${BASE}/publish`, data),

    updateJob: (data: {
        jobPostingId: string;
        redirectUrl?: string;
        completionEmail?: string;
        boards?: { id: number; durationDays?: number }[];
        countryCode?: string;
        sublocationId?: number;
    }) => api.put<PublishResult>(`${BASE}/publish`, data),

    deleteJob: (jobPostingId: string) =>
        api.delete<{ deleted: boolean }>(`${BASE}/publish/${jobPostingId}`),

    deletePost: (jobPostingId: string, portalId: number) =>
        api.delete<{ boardPublishing: BoardPublishing }>(`${BASE}/publish/${jobPostingId}/boards/${portalId}`),

    getPostingStatus: (jobPostingId: string) =>
        api.get<{ boardPublishing: BoardPublishing | null }>(`${BASE}/status/${jobPostingId}`),

    syncPostingStatus: (jobPostingId: string) =>
        api.post<{ boardPublishing: BoardPublishing }>(`${BASE}/status/${jobPostingId}/sync`),

    getConfig: () =>
        api.get<IdibuConfigResponse>(`${BASE}/config`),

    updateConfig: (data: { accountHash: string }) =>
        api.patch<{ config: IdibuOrgConfig }>(`${BASE}/config`, data),

    reconnect: () =>
        api.post<{ config: IdibuOrgConfig }>(`${BASE}/reconnect`),

    provisionAccount: (data: ProvisionData) =>
        api.post<{ config: IdibuOrgConfig }>(`${BASE}/provision`, data),

    disconnectConfig: () =>
        api.delete<{ disconnected: boolean }>(`${BASE}/config`),

    getPortalSubscribeInfo: (portalId: number) =>
        api.get<PortalSubscribeInfoResponse>(`${BASE}/portals/${portalId}/subscribe-info`),

    subscribePortal: (portalId: number, fields: { name: string; value: string }[]) =>
        api.post(`${BASE}/portals/${portalId}/subscribe`, { fields }),

    unsubscribePortal: (portalId: number) =>
        api.post(`${BASE}/portals/${portalId}/unsubscribe`),

    getPortalCategories: () =>
        api.get<{ categories: PortalCategory[] }>(`${BASE}/portals/categories`),

    getPortalCountries: () =>
        api.get<{ countries: PortalCountry[] }>(`${BASE}/portals/countries`),

    listProvisionedAccounts: (params?: { count?: number; offset?: number }) =>
        api.get<{ accounts: ProvisionedAccount[] }>(`${BASE}/accounts`, { params }),

    syncApplicantStatus: (applicationId: string) =>
        api.post<ApplicantSyncResult>(`${BASE}/applicants/${applicationId}/sync-status`),

    bulkSyncStatuses: (jobPostingId: string) =>
        api.post<{ total: number; synced: number; alreadySynced: number; noAction: number; failed: number }>(`${BASE}/applicants/bulk-sync/${jobPostingId}`),

    reconcile: (data?: { afterDate?: string; allTime?: boolean; dryRun?: boolean }) =>
        api.post<ReconcileResult>(`${BASE}/reconcile`, data),
};

/**
 * Demo-mode shim — when `IDIBU_DEMO_MODE` is true, calls are served by a
 * local fixture API instead of hitting `/v2/idibu`. The shape is identical
 * (`{ data }` envelope) so consumers don't know the difference.
 *
 * Revert: set `IDIBU_DEMO_MODE = false` in ../demo/demo-flag.ts, or delete
 * the demo/ folder and remove these last lines + the two imports above.
 */
export const idibuApi: typeof realIdibuApi = IDIBU_DEMO_MODE
    ? (demoIdibuApi as unknown as typeof realIdibuApi)
    : realIdibuApi;
